import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, CardBody, Col, Container, Row } from "reactstrap";
import { MafiaPageHeader, StatTile } from "./_shared";
import MafiaTheme from "./MafiaTheme";
import api from "../../api";
import { AppDialogProvider, useAppDialog } from "./AppDialogProvider";

import UncheckedParts from "./UncheckedParts";
import AllParts from "./AllParts";
import Gages from "./Gages";
import Print from "./Print";
import PIR from "./PIR";
import Unreleased from "./Unreleased";
import FrmJob from "./FrmJob";
import ImportFromNJ from "./ImportFromNJ";
import NewPart from "./NewPart";
import ConfirmPart from "./ConfirmPart";
import ReleasePart from "./ReleasePart";
import EditPart from "./EditPart";
import FrmSelect from "./FrmSelect";
import MafiaAdmin from "./MafiaAdmin";
// MAFIA Aux Simulator round — legacy parity: the MAFIA Auxiliary Toolbox
// "Simulator" button opened the Digital Inspection Simselect workflow
// (SWhite-Digital-Inspection/Simselect.vb: all-parts grid → pick a part →
// read-only Inspect with simulated job "12345" / qty 500, saves disabled).
// The modern DI SimSelect component already reproduces that flow, so the
// MAFIA tile reuses it in-dashboard (embedded) instead of launching the
// legacy WinForms EXE. No duplication; DI's /di/simulator route unchanged.
import SimSelect from "../DI/Inspection/SimSelect";
import { getMafiaCountryCode, getMafiaAccessLevel, getMafiaIsAdmin } from "./mafiaIdentity";
import { resetMafiaIdentity } from "./mafiaIdentity";
// Round MAFIA-Country-1 — working-database pin (X-Mafia-Country header).
import { setMafiaCountry } from "./mafiaCountry";

const INITIAL_BUTTONS = {
  uncheckedParts: {
    label: "Unchecked Parts",
    subtitle: "Review parts waiting for validation",
    icon: "ri-checkbox-circle-line",
    tone: "primary",
    authorized: false,
  },
  allParts: {
    label: "All Parts",
    subtitle: "Browse and maintain the full catalog",
    icon: "ri-stack-line",
    tone: "info",
    authorized: false,
  },
  gages: {
    label: "Gages",
    subtitle: "Manage gage-related records",
    icon: "ri-ruler-2-line",
    tone: "success",
    authorized: false,
  },
  pir: {
    // Round 59 — renamed to match the legacy MAFIA Auxiliary Toolbox button
    // "Print Digital Inspection Record (DIR) Beta" per UAT feedback ("change
    // the same as like legacy"). Backend routes stay "pir" (allowed).
    label: "Print Digital Inspection Record (DIR)",
    subtitle: "Print Digital Inspection Records by job or PO/Line",
    icon: "ri-file-list-3-line",
    tone: "warning",
    authorized: false,
  },
  release: {
    label: "Release",
    subtitle: "Release approved parts to the next step",
    icon: "ri-rocket-line",
    tone: "success",
    authorized: false,
  },
  print: {
    label: "Print MAFIA Table",   // Round 3a (owner): renamed from "Print"
    subtitle: "Generate outputs and print resources",
    icon: "ri-printer-line",
    tone: "dark",
    authorized: false,
  },
  simulate: {
    label: "Simulator",
    subtitle: "Run a read-only inspection simulation",
    icon: "ri-cpu-line",
    tone: "secondary",
    authorized: false,
  },
  job: {
    label: "Job",
    subtitle: "Work with job-specific operations",
    icon: "ri-briefcase-4-line",
    tone: "primary",
    authorized: false,
  },
  manual: {
    label: "Create New MAFIA",
    subtitle: "Create a new record manually",
    icon: "ri-add-circle-line",
    tone: "danger",
    authorized: false,
  },
  importNj: {
    // Round MAFIA-UAT-1 (2026) — renamed per UAT: the remote source for a
    // USA user IS India (the "NJ" name is a leftover from when the remote
    // site was New Jersey). India users see the reverse label below.
    label: "Import From India",
    subtitle: "Import parts from the India MAFIA database",
    icon: "ri-download-cloud-2-line",
    tone: "info",
    authorized: false,
  },
  admin: {
    label: "Permissions Admin",
    subtitle: "Manage Mafia Aux access (IT Admin only)",
    icon: "ri-shield-user-line",
    tone: "dark",
    authorized: false,
  },
};

const MENU_ORDER = [
  "uncheckedParts",
  "allParts",
  "gages",
  "pir",
  "release",
  "print",
  "job",
  "manual",
  "importNj",
  "simulate",
  "admin",
];

// Tile groupings — visual hierarchy so the menu reads as categories instead
// of one flat 11-tile grid. Order within each group preserved from MENU_ORDER.
const TILE_GROUPS = [
  {
    key: "review",
    title: "Review & Approve",
    description: "Parts moving through the verification pipeline.",
    icon: "ri-checkbox-circle-line",
    keys: ["uncheckedParts", "allParts", "release"],
  },
  {
    key: "create",
    title: "Create & Modify",
    description: "Author new MAFIA records and edit existing ones.",
    icon: "ri-edit-box-line",
    keys: ["manual", "job", "importNj"],
  },
  {
    key: "tools",
    title: "Tools & Output",
    description: "Gages, print, DIR, and simulation utilities.",
    icon: "ri-tools-line",
    keys: ["gages", "pir", "print", "simulate"],
  },
  {
    key: "admin",
    title: "Administration",
    description: "Manage permissions (IT Admin only).",
    icon: "ri-shield-user-line",
    keys: ["admin"],
  },
];

function MenuPageContent() {
  const dialog = useAppDialog();

  const [countryCode, setCountryCode] = useState("");
  const [permissionLevel, setPermissionLevel] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState("menu");
  const [pageProps, setPageProps] = useState({});

  // Landing-page stats. Pulled in parallel with permission load so the
  // hero numbers are populated by the time tiles render. Failures are
  // soft — stats just stay at 0 and the rest of the page works.
  const [stats, setStats] = useState({
    unchecked: 0,
    unreleased: 0,
    inProgress: 0,
    allParts: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    initializePage();
  }, []);


  // Round MAFIA-Country-1 hotfix 2 — the "can't switch back" wedge.
  // The four hero-stat requests had no timeout and no cancellation; when
  // the India database is slow or briefly locked (e.g. during the index
  // build), they occupy the browser's per-origin connection pool, the
  // next whoami QUEUES behind them, initializePage never resolves,
  // `loading` stays true and the switcher buttons stay disabled — until a
  // refresh drops the sockets. Fix: every switch ABORTS the previous
  // country's in-flight stat requests (freeing the sockets immediately),
  // all stat calls carry a hard client timeout, and a sequence counter
  // makes sure a LATE response from the old country can never overwrite
  // the new country's identity or numbers.
  const statsAbortRef = useRef(null);
  const loadSeqRef = useRef(0);

  // Round MAFIA-Country-1 — manual working-database switch. Re-pins the
  // X-Mafia-Country header, drops the cached whoami, and reloads identity
  // + hero stats from the newly selected database. Round MAFIA-Country-2
  // (owner decision): AccessLevel is CENTRALIZED — always resolved from
  // the USA MafiaAuxPermissions regardless of the working database — so
  // the level will read the same in both modes; the refetch still
  // confirms the effective country and refreshes the stats. The switch is
  // only offered on the menu hub (no in-flight edit state to lose here);
  // pages opened afterwards inherit the pin.
  async function handleSwitchCountry(next) {
    const target = String(next || "").trim().toUpperCase();
    if (target !== "USA" && target !== "INDIA") return;
    if (target === countryCode) return;
    // Free the connection pool NOW — hung previous-country stat requests
    // must not delay the new country's whoami.
    try {
      statsAbortRef.current?.abort();
    } catch {
      /* already settled */
    }
    setMafiaCountry(target);
    resetMafiaIdentity();
    setLoading(true);
    setStatsLoading(true);
    await initializePage();
  }

  async function initializePage() {
    const seq = ++loadSeqRef.current;
    try {
      const country = await getCountryCode();
      const permission = await getMyPermissionLevel();
      const admin = await getMafiaIsAdmin();
      if (seq !== loadSeqRef.current) return; // superseded by a newer switch
      setCountryCode(country);
      setPermissionLevel(permission);
      setIsAdmin(admin);
      // Round MAFIA-Country-1: keep the pin in lock-step with the
      // SERVER-CONFIRMED effective country from whoami (selection header →
      // profile claim), so every subsequent mafia/vantage call from any
      // page identifies the same working database.
      setMafiaCountry(country);
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      console.error(err);
      await dialog.error("Failed to initialize MAFIA Aux.");
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }

    // Pull landing-page stats in parallel. Each call uses the new optional
    // `?limit=1` param when supported — we only care about row counts here,
    // not the data. Failures are soft.
    fetchLandingStats(seq);
  }

  async function fetchLandingStats(seq) {
    // Round MAFIA-Country-1 hotfix 2: abortable + hard 15s client timeout
    // per call + sequence guard. These are vanity counts — they must never
    // be able to wedge the page or overwrite another country's numbers.
    const controller = new AbortController();
    statsAbortRef.current = controller;
    const opts = { signal: controller.signal, timeout: 15000 };
    setStatsLoading(true);
    try {
      // Use Promise.allSettled so a single failing endpoint doesn't kill
      // the rest of the stat row.
      // NOTE: the All Parts count comes from `edit-parts` (which returns an
      // accurate deduped TotalCount), NOT from `all-parts` — `all-parts` is
      // capped at TOP 1000, so its .length maxes out at 1000 and shows a wrong
      // total (e.g. "1,000" instead of the real ~9,559). edit-parts?pageSize=1
      // fetches just the count cheaply.
      const [unchecked, unreleased, inProgress, allParts] = await Promise.allSettled([
        api.get("/mafia/unchecked-parts", opts),
        api.get("/mafia/unreleased-parts", opts),
        api.get("/mafia/edit-parts-inprogress", opts),
        api.get("/mafia/edit-parts?page=1&pageSize=1", opts),
      ]);

      if (seq !== undefined && seq !== loadSeqRef.current) return; // stale country

      // edit-parts returns { items, totalCount }; fall back to array length for
      // the other three (they return plain lists).
      const allPartsTotal =
        allParts.status === "fulfilled"
          ? (allParts.value.data?.totalCount ??
             (Array.isArray(allParts.value.data) ? allParts.value.data.length : 0))
          : 0;

      setStats({
        unchecked: unchecked.status === "fulfilled" ? (unchecked.value.data?.length || 0) : 0,
        unreleased: unreleased.status === "fulfilled" ? (unreleased.value.data?.length || 0) : 0,
        inProgress: inProgress.status === "fulfilled" ? (inProgress.value.data?.length || 0) : 0,
        allParts: allPartsTotal,
      });
    } catch (err) {
      // soft-fail — stats stay at 0
      console.error("Failed to load Mafia landing stats", err);
    } finally {
      if (seq === undefined || seq === loadSeqRef.current) setStatsLoading(false);
    }
  }

  // ── Legacy permission tier table (Menu.vb lines 225-342) ─────────────────
  //
  // Direct port of legacy `myPerm` semantics. Lower number = MORE privilege:
  //   1 = "Beta Testers & Full Functionality" (all buttons + releaser)
  //   2 = "Releasers" (same buttons as tier 1; both grant releaser flag)
  //   3 = "Gage Capability" (no release, no job)
  //   4 = "Engineers"      (no release, no job, no gages)
  //   0/anything else = "Non Credentialed" (PIR + Print + Simulate only)
  //
  // USA and India tier tables in legacy are identical for these buttons;
  // small font/label differences are not security-relevant.
  const TIER_GATES = {
    1: { uncheckedParts: true,  allParts: true,  gages: true,  pir: true, release: true,  print: true, simulate: true, job: true,  manual: true,  importNj: true  },
    2: { uncheckedParts: true,  allParts: true,  gages: true,  pir: true, release: true,  print: true, simulate: true, job: true,  manual: true,  importNj: true  },
    3: { uncheckedParts: true,  allParts: true,  gages: true,  pir: true, release: false, print: true, simulate: true, job: false, manual: true,  importNj: true  },
    4: { uncheckedParts: true,  allParts: true,  gages: false, pir: true, release: false, print: true, simulate: true, job: false, manual: true,  importNj: true  },
    0: { uncheckedParts: false, allParts: false, gages: false, pir: true, release: false, print: true, simulate: true, job: false, manual: false, importNj: false },
  };

  // Releaser flag (Menu.vb line 237, 250, 296, 309) — only tier 1 and 2.
  // Print.vb line 13-19 uses this to show/hide the Delete button.
  const isReleaser = useMemo(
    () => permissionLevel === 1 || permissionLevel === 2,
    [permissionLevel]
  );

  const computedButtons = useMemo(() => {
    const buttons = JSON.parse(JSON.stringify(INITIAL_BUTTONS));
    const tier = TIER_GATES[permissionLevel] || TIER_GATES[0];

    Object.keys(buttons).forEach((key) => {
      // tier table is the source of truth for everything except `admin`.
      if (key in tier) {
        buttons[key].authorized = tier[key] === true;
      } else {
        // keys NOT in tier table (currently just `admin`) default off; the
        // admin gate is applied below from isAdmin.
        buttons[key].authorized = false;
      }
    });

    // Permissions Admin is gated on the IT Admin dashboard role, not the
    // Mafia access level. Hiding the button is only a UX hint — every
    // admin/* endpoint is independently enforced server-side with
    // [Authorize(Roles = "IT Admin")].
    //
    // Round MAFIA-Country-1 hotfix — IT Admin unlocks EVERY tile, not just
    // the admin card. The backend has always bypassed the AccessLevel
    // ladder for IT Admins (RequireMafiaLevelAsync returns null before the
    // MafiaAuxPermissions lookup), so an IT Admin could already do
    // everything via the API while the tiles showed locked. The tiles now
    // match the server truth.
    if (isAdmin) {
      Object.keys(buttons).forEach((key) => {
        buttons[key].authorized = true;
      });
    }

    // Round MAFIA-UAT-1 (2026) — "Import From NJ / India" → country-aware:
    // USA (and unknown) users import FROM India; India users import FROM
    // Florida (legacy ImportFromNJ.vb set the window title the same way).
    if (countryCode === "INDIA") {
      buttons.importNj.label = "Import From Florida";
      buttons.importNj.subtitle = "Import parts from the Florida MAFIA database";
    }

    return buttons;
  }, [countryCode, permissionLevel, isAdmin]);

  const environmentText = useMemo(() => {
    if (countryCode === "USA") return "USA Database";
    if (countryCode === "INDIA") return "India Database";
    return "Unknown Database";
  }, [countryCode]);

  const accessibleCount = useMemo(
    () => Object.values(computedButtons).filter((item) => item.authorized).length,
    [computedButtons]
  );

  const visibleButtons = useMemo(() => {
    return MENU_ORDER.filter((key) => computedButtons[key]).map((key) => ({
      key,
      ...computedButtons[key],
    }));
  }, [computedButtons]);

  function openPage(pageKey, props = {}) {
    setPageProps(props);
    setActivePage(pageKey);
  }

  function goBackToMenu() {
    setPageProps({});
    setActivePage("menu");
  }

  function handleMenuClick(key) {
    const button = computedButtons[key];
    if (!button?.authorized) return;

    // MAFIA Aux Simulator round — no more external-EXE placeholder. The
    // tile now opens the in-dashboard simulator (embedded DI SimSelect),
    // same as every other Mafia sub-page.
    if (key === "simulate") {
      openPage("simulate");
      return;
    }

    if (key === "release") {
      openPage("release");
      return;
    }

    openPage(key);
  }

  function renderActivePage() {
    switch (activePage) {
      case "uncheckedParts":
        return (
          <UncheckedParts
            onBack={goBackToMenu}
            onOpenConfirmPart={(partData) => openPage("confirmPart", partData)}
          />
        );

      case "allParts":
        return (
          <AllParts
            onBack={goBackToMenu}
            onOpenEditPart={(partData) => openPage("editPart", partData)}
          />
        );

      case "gages":
        return <Gages onBack={goBackToMenu} />;

      case "simulate":
        // Legacy MAFIA "Simulator" = DI Simselect.vb workflow. Reuse the
        // modern DI SimSelect read-only screen inside the Mafia shell:
        // embedded (MenuPage already provides page-content + Container),
        // with a MAFIA breadcrumb and Back-to-Menu. Access rule preserved:
        // the user reached here through the app.mafia route gate and the
        // tile's tier table (legacy allowed simulate for every tier incl.
        // non-credentialed); the screen itself is strictly read-only.
        return (
          <SimSelect
            embedded
            onBack={goBackToMenu}
            breadcrumb={[
              { label: "Applications", to: "/" },
              { label: "MAFIA Aux", onClick: goBackToMenu },
              { label: "Simulator" },
            ]}
          />
        );

      case "print":
        return <Print onBack={goBackToMenu} />;

      case "pir":
        return <PIR onBack={goBackToMenu} />;

      case "release":
        return (
          <Unreleased
            onBack={goBackToMenu}
            onOpenReleasePart={(partData) => openPage("releasePart", partData)}
          />
        );

      case "job":
        return <FrmJob onBack={goBackToMenu} />;

      case "admin":
        return <MafiaAdmin onBack={goBackToMenu} />;

      case "importNj":
        return (
          <ImportFromNJ
            onBack={goBackToMenu}
            onOpenEditPart={(partData) => openPage("editPart", partData)}
          />
        );

      case "manual":
        return (
          <NewPart
            onBack={goBackToMenu}
            onOpenEditPart={(partData) => openPage("editPart", partData)}
          />
        );

      case "confirmPart":
        return (
          <ConfirmPart
            partNum={pageProps.partNum}
            rev={pageProps.rev}
            revMinor={pageProps.revMinor}
            onBack={() => openPage("uncheckedParts")}
            onConfirmed={() => openPage("uncheckedParts")}
          />
        );

      case "releasePart":
        return (
          <ReleasePart
            partNum={pageProps.partNum}
            rev={pageProps.rev}
            revMinor={pageProps.revMinor}
            onBack={() => openPage("release")}
            onReleased={() => openPage("release")}
          />
        );

      case "editPart":
        return (
          <EditPart
            partNum={pageProps.partNum}
            rev={pageProps.rev}
            revMinor={pageProps.revMinor}
            isImported={pageProps.isImported}
            isNew={pageProps.isNew}
            onBack={goBackToMenu}
            onOpenFrmSelect={(data) => openPage("frmSelect", data)}
            onDone={goBackToMenu}
          />
        );

      case "frmSelect":
        return (
          <FrmSelect
            partnum={pageProps.partnum}
            revMajor={pageProps.revMajor}
            revMinor={pageProps.revMinor}
            createdBy={pageProps.createdBy}
            onBack={goBackToMenu}
            onFinish={goBackToMenu}
          />
        );

      default:
        return null;
    }
  }

  function renderTile(button) {
    const toneColor = button.tone || "primary";
    const isDisabled = !button.authorized;

    return (
      <Col xxl={3} xl={4} md={6} key={button.key} className="mb-3">
        <Card
          className={`h-100 mafia-tile ${isDisabled ? "mafia-tile-disabled" : ""}`}
          // Round 61 (UX): a locked tile now says WHY on hover instead of
          // just looking faded.
          title={
            isDisabled
              ? `Not available at your MAFIA access level (Level ${permissionLevel}). Contact IT Admin if you need this.`
              : undefined
          }
          onClick={() => !isDisabled && handleMenuClick(button.key)}
          role={isDisabled ? undefined : "button"}
          tabIndex={isDisabled ? -1 : 0}
          onKeyDown={(e) => {
            if (!isDisabled && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              handleMenuClick(button.key);
            }
          }}
          style={{
            cursor: isDisabled ? "not-allowed" : "pointer",
            opacity: isDisabled ? 0.55 : 1,
            transition:
              "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
            border: "1px solid transparent",
            borderRadius: 12,
          }}
        >
          <CardBody className="p-3">
            <div className="d-flex align-items-start gap-3">
              <div
                className="avatar-sm flex-shrink-0"
                aria-hidden="true"
              >
                <div
                  className={`avatar-title rounded bg-${toneColor}-subtle text-${toneColor} fs-3`}
                  style={{ borderRadius: 10 }}
                >
                  <i className={button.icon} />
                </div>
              </div>
              <div className="flex-grow-1 overflow-hidden">
                <h6 className="mb-1 fw-semibold text-truncate" style={{ color: "#212529" }}>
                  {button.label}
                </h6>
                <p className="text-muted small mb-0" style={{ fontSize: 12 }}>
                  {button.subtitle}
                </p>
              </div>
              {!isDisabled && (
                <i
                  className="ri-arrow-right-s-line text-muted flex-shrink-0 mafia-tile-arrow"
                  style={{ fontSize: 22 }}
                  aria-hidden="true"
                />
              )}
              {isDisabled && (
                <i
                  className="ri-lock-2-line text-muted flex-shrink-0"
                  style={{ fontSize: 18, opacity: 0.6 }}
                  aria-hidden="true"
                  title="You don't have permission for this action"
                />
              )}
            </div>
          </CardBody>
        </Card>
      </Col>
    );
  }

  function renderTileGroup(group) {
    const tiles = group.keys
      .filter((k) => computedButtons[k])
      .map((k) => ({ key: k, ...computedButtons[k] }));

    // Hide groups whose buttons are all unauthorized AND not displayable.
    // Legacy showed disabled buttons; we preserve that — only hide group
    // if it has zero tiles at all (e.g. admin group when user isn't admin).
    if (tiles.length === 0) return null;

    // For the admin group: hide entirely if not authorized (no point
    // showing a disabled lone tile).
    if (group.key === "admin" && tiles.every((t) => !t.authorized)) return null;

    return (
      <div key={group.key} className="mb-4">
        <div className="d-flex align-items-center gap-2 mb-2 px-1">
          <i
            className={group.icon}
            style={{ fontSize: 16, color: "#405189" }}
          />
          <h6 className="fw-semibold mb-0" style={{ color: "#212529" }}>
            {group.title}
          </h6>
          <span
            className="text-muted small"
            style={{ fontSize: 12, marginLeft: 8 }}
          >
            {group.description}
          </span>
        </div>
        <Row className="g-3">{tiles.map(renderTile)}</Row>
      </div>
    );
  }

  function renderMenu() {
    const headerActions = (
      <>
        {/* Round MAFIA-Country-1 — the environment badge is now a
            working-database SWITCHER (legacy had auto-detect only; the
            modern requirement adds a manual switch for everyone,
            irrespective of location). The active database is highlighted;
            clicking the other one re-pins X-Mafia-Country, clears the
            cached identity and reloads identity/stats from the newly
            selected database. AccessLevel is centralized (always the USA
            MafiaAuxPermissions — owner decision), so the level reads the
            same in both modes. */}
        <div
          className="btn-group"
          role="group"
          aria-label="Working MAFIA database"
          style={{ borderRadius: 8, overflow: "hidden" }}
        >
          {["USA", "INDIA"].map((c) => {
            const active = countryCode === c;
            return (
              <button
                key={c}
                type="button"
                className="btn btn-sm"
                disabled={loading}
                onClick={() => handleSwitchCountry(c)}
                style={{
                  background: active
                    ? "rgba(255,255,255,0.95)"
                    : "rgba(255,255,255,0.18)",
                  color: active ? "#405189" : "#fff",
                  padding: "6px 12px",
                  fontSize: 12,
                  fontWeight: active ? 700 : 500,
                  letterSpacing: "0.02em",
                  border: "none",
                }}
                title={
                  active
                    ? `Working in the ${c === "INDIA" ? "India" : "USA"} MAFIA database`
                    : `Switch to the ${c === "INDIA" ? "India" : "USA"} MAFIA database`
                }
              >
                <i className="ri-global-line align-bottom me-1" />
                {c === "INDIA" ? "India Database" : "USA Database"}
              </button>
            );
          })}
        </div>
        <span
          className="badge"
          style={{
            background: "rgba(255,255,255,0.18)",
            color: "#fff",
            padding: "6px 12px",
            fontSize: 12,
            fontWeight: 500,
            letterSpacing: "0.02em",
          }}
        >
          <i className="ri-shield-user-line align-bottom me-1" />
          Level {permissionLevel}
        </span>
        <span
          className="badge"
          style={{
            background: "rgba(255,255,255,0.18)",
            color: "#fff",
            padding: "6px 12px",
            fontSize: 12,
            fontWeight: 500,
            letterSpacing: "0.02em",
          }}
        >
          <i className="ri-apps-2-line align-bottom me-1" />
          {accessibleCount} authorized
        </span>
      </>
    );

    return (
      <div className="page-content mafia-screen">
        <MafiaTheme />
        <Container fluid>
          <MafiaPageHeader
            icon="ri-stack-line"
            eyebrow="Production Tools"
            title="MAFIA Aux"
            subtitle="Verify, release, and manage MAFIA tables across the pipeline."
            tone="default"
            actions={headerActions}
            breadcrumb={[
              { label: "Applications", to: "/" },
              { label: "MAFIA Aux", to: "/mafia" },
            ]}
          />

          {/* Stat row — the four numbers users care about at a glance. */}
          <Row className="g-3 mb-4">
            <Col xl={3} md={6}>
              <StatTile
                icon="ri-checkbox-circle-line"
                label="Awaiting Verification"
                value={stats.unchecked}
                tone="warning"
                loading={statsLoading}
                onClick={() => handleMenuClick("uncheckedParts")}
              />
            </Col>
            <Col xl={3} md={6}>
              <StatTile
                icon="ri-rocket-line"
                label="Ready to Release"
                value={stats.unreleased}
                tone="info"
                loading={statsLoading}
                onClick={
                  computedButtons.release?.authorized
                    ? () => handleMenuClick("release")
                    : undefined
                }
              />
            </Col>
            <Col xl={3} md={6}>
              <StatTile
                icon="ri-loader-2-line"
                label="In Progress"
                value={stats.inProgress}
                tone="primary"
                loading={statsLoading}
              />
            </Col>
            <Col xl={3} md={6}>
              <StatTile
                icon="ri-stack-line"
                label="All Parts"
                value={stats.allParts}
                tone="success"
                loading={statsLoading}
                onClick={
                  computedButtons.allParts?.authorized
                    ? () => handleMenuClick("allParts")
                    : undefined
                }
              />
            </Col>
          </Row>

          {/* Tile groups */}
          {TILE_GROUPS.map(renderTileGroup)}

          <style>{`
            .mafia-tile:hover:not(.mafia-tile-disabled) {
              transform: translateY(-2px);
              box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06) !important;
              border-color: rgba(64, 81, 137, 0.18) !important;
            }
            .mafia-tile:hover:not(.mafia-tile-disabled) .mafia-tile-arrow {
              color: #405189 !important;
              transform: translateX(2px);
              transition: transform 0.15s ease, color 0.15s ease;
            }
            .mafia-tile-disabled {
              /* Round 61 (UX): pointer-events:none also suppressed the
                 hover tooltip explaining WHY the tile is locked. Clicks
                 are already guarded in handleMenuClick/onClick, so hover
                 can stay enabled for the title tooltip. */
              cursor: not-allowed;
            }
          `}</style>
        </Container>
      </div>
    );
  }


  function renderChildPage() {
    return (
      <div className="page-content mafia-screen">
        <MafiaTheme />
        <Container fluid>
          {renderActivePage()}
        </Container>
      </div>
    );
  }

  return loading ? (
    <div className="page-content mafia-screen">
        <MafiaTheme />
      <Container fluid>
        <MafiaPageHeader
          icon="ri-stack-line"
          eyebrow="Production Tools"
          title="MAFIA Aux"
          subtitle="Loading your workspace..."
          tone="default"
        />
        <Row className="g-3 mb-4">
          {[1, 2, 3, 4].map((n) => (
            <Col xl={3} md={6} key={n}>
              <StatTile
                icon="ri-stack-line"
                label="Loading"
                value={0}
                tone="secondary"
                loading={true}
                subtle
              />
            </Col>
          ))}
        </Row>
      </Container>
    </div>
  ) : activePage !== "menu" ? (
    renderChildPage()
  ) : (
    renderMenu()
  );
}

export default function MenuPage() {
  return (
    <AppDialogProvider>
      <MenuPageContent />
    </AppDialogProvider>
  );
}

async function getCountryCode() {
  return getMafiaCountryCode();
}

async function getMyPermissionLevel() {
  return getMafiaAccessLevel();
}

// MAFIA Aux Simulator round: the launchSimulator() external-EXE placeholder
// was removed — the "simulate" tile now opens the embedded DI SimSelect
// (see renderActivePage case "simulate"). No desktop launcher required.